/**
 * Compact avatar + label for snapshot creator / restorer rows.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';

import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';

import {
  snapshotActorPrimaryLabel,
  snapshotActorTooltip,
  snapshotUserInitials,
} from '../format-snapshot';

export interface SnapshotActorBadgeProps {
  displayName?: string | null;
  email?: string | null;
  variant?: 'muted' | 'warning';
  /** Tighter avatar + text for dense list rows */
  compact?: boolean;
}

const SnapshotActorBadge: FC<SnapshotActorBadgeProps> = ({
  displayName,
  email,
  variant = 'muted',
  compact = false,
}) => {
  const label = snapshotActorPrimaryLabel(displayName, email);
  const tooltip = snapshotActorTooltip(displayName, email);
  const initials = snapshotUserInitials(label);

  const inner = (
    <span
      className={cn(
        'inline-flex min-w-0 max-w-full items-center rounded-sm',
        compact ? 'gap-1' : 'gap-1.5 py-0.5',
        variant === 'warning' ? 'text-warning' : 'text-muted-foreground',
      )}
    >
      <span
        className={cn(
          'flex shrink-0 items-center justify-center rounded-full font-semibold uppercase leading-none',
          compact ? 'h-4 w-4 text-[7px]' : 'h-5 w-5 text-[9px]',
          variant === 'warning'
            ? 'bg-warning/25 text-warning'
            : 'bg-muted text-muted-foreground',
        )}
        aria-hidden
      >
        {initials}
      </span>
      <span
        className={cn(
          'truncate',
          compact ? 'max-w-[10rem] text-[10px] font-normal' : 'font-medium',
          variant === 'warning' ? 'text-warning' : 'text-foreground',
        )}
      >
        {label}
      </span>
    </span>
  );

  if (!tooltip || tooltip === label) return inner;

  return (
    <Tooltip content={tooltip} side="top" interactive contentClassName="max-w-sm">
      {inner}
    </Tooltip>
  );
};

export default SnapshotActorBadge;
